/**
 * Markdown Renderer Component
 * Renders markdown content with proper styling and XSS sanitization
 */

import { useMemo } from "react";
import DOMPurify from "dompurify";

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * DOMPurify configuration for strict HTML sanitization.
 * Only allows safe tags and attributes for markdown content.
 */
const DOMPURIFY_CONFIG = {
  // Allow only safe HTML tags for markdown content
  ALLOWED_TAGS: [
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "p",
    "br",
    "hr",
    "ul",
    "ol",
    "li",
    "blockquote",
    "pre",
    "code",
    "a",
    "strong",
    "em",
    "b",
    "i",
    "u",
    "s",
    "del",
    "ins",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "img",
    "figure",
    "figcaption",
    "div",
    "span",
    "section",
    "article",
    "dl",
    "dt",
    "dd",
    "sub",
    "sup",
    "mark",
    "abbr",
  ],
  // Allow only safe attributes
  ALLOWED_ATTR: [
    "href",
    "src",
    "alt",
    "title",
    "class",
    "id",
    "name",
    "target",
    "rel",
    "width",
    "height",
    "loading",
    "colspan",
    "rowspan",
    "scope",
    "aria-label",
    "aria-describedby",
    "role",
  ],
  // Block dangerous URI schemes using negative lookahead (blocklist approach)
  // This is more robust than allowlisting because:
  // - Dangerous schemes (javascript:, data:, etc.) are a known finite set
  // - Legitimate paths can have colons anywhere: lesson:overview.md, page?v=12:30
  // Blocks: javascript:, data:, vbscript:, about:, blob: and any URI with leading whitespace
  // Allows: All other URIs including relative paths with colons in filenames/queries
  ALLOWED_URI_REGEXP: /^(?!(javascript|data|vbscript|about|blob):)(?!\s).*/i,
  // Prevent DOM clobbering attacks
  SANITIZE_DOM: true,
  // Remove dangerous content
  FORBID_TAGS: [
    "script",
    "style",
    "iframe",
    "object",
    "embed",
    "form",
    "input",
    "button",
    "select",
    "textarea",
  ],
  FORBID_ATTR: [
    "onerror",
    "onload",
    "onclick",
    "onmouseover",
    "onfocus",
    "onblur",
  ],
};

/**
 * Sanitizes HTML content using DOMPurify with strict allowlist.
 * This properly handles entity decoding, xlink: attributes, srcset, and other XSS vectors
 * that regex-based sanitizers miss.
 */
function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, DOMPURIFY_CONFIG) as string;
}

export function MarkdownRenderer({
  content,
  className = "",
}: MarkdownRendererProps) {
  // Memoize sanitization to avoid re-running on every render
  const sanitizedContent = useMemo(() => sanitizeHtml(content), [content]);

  return (
    <div
      className={`prose prose-gray dark:prose-invert max-w-none
        prose-headings:font-semibold prose-headings:scroll-mt-20
        prose-h1:text-3xl prose-h1:mb-6 prose-h1:pb-2 prose-h1:border-b prose-h1:border-gray-200 dark:prose-h1:border-gray-700
        prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-100 dark:prose-h2:border-gray-800
        prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
        prose-p:text-gray-600 dark:prose-p:text-gray-300 prose-p:leading-relaxed
        prose-a:text-primary-600 dark:prose-a:text-primary-400 prose-a:no-underline hover:prose-a:underline
        prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-medium prose-code:before:content-none prose-code:after:content-none
        prose-pre:bg-gray-900 dark:prose-pre:bg-gray-950 prose-pre:text-gray-100 dark:prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:shadow-sm dark:prose-pre:shadow-gray-950/50
        [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-inherit [&_pre_code]:text-gray-100 dark:[&_pre_code]:text-gray-100
        prose-li:text-gray-600 dark:prose-li:text-gray-300 prose-li:my-1
        prose-ul:my-4 prose-ol:my-4
        prose-blockquote:border-l-4 prose-blockquote:border-primary-500 prose-blockquote:bg-gray-50 dark:prose-blockquote:bg-gray-800/50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
        prose-table:border-collapse prose-table:w-full prose-table:my-6
        prose-thead:bg-gray-100 dark:prose-thead:bg-gray-800
        prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:px-4 prose-th:py-3 prose-th:text-left prose-th:font-semibold prose-th:text-gray-900 dark:prose-th:text-gray-100
        prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 prose-td:px-4 prose-td:py-3
        prose-tr:even:bg-gray-50 dark:prose-tr:even:bg-gray-800/30
        prose-hr:my-8 prose-hr:border-gray-200 dark:prose-hr:border-gray-700
        prose-img:rounded-lg prose-img:shadow-md
        ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
}
